"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RundownSchema = void 0;
const zod_1 = require("zod");
const RundownColumnType = zod_1.z.enum([
    'backTiming',
    'checkbox',
    'cumulativeDuration',
    'duration',
    'frontTiming',
    'itemNumber',
    'text',
    'longText',
    'snapstreamLink',
    'readonlyBlockId',
    'readonlyScriptId',
]);
const GridBlobKey = zod_1.z.custom((val) => typeof val === 'string' && /^blobData\./.test(val));
const RundownColDef = zod_1.z.object({
    colId: zod_1.z.string(),
    field: GridBlobKey.optional(),
    rundownColumnType: RundownColumnType,
    headerName: zod_1.z.string().optional(),
    headerTooltip: zod_1.z.string().optional(),
    width: zod_1.z.number().int().positive().optional(),
});
const RundownRowType = zod_1.z.enum(['header', 'element']);
const LevelConfig = zod_1.z.object({
    enableInsert: zod_1.z.boolean(),
    displayName: zod_1.z.string().min(1),
    rowTypeId: RundownRowType.optional(),
});
const RowLevel = zod_1.z.union([zod_1.z.literal(1), zod_1.z.literal(2), zod_1.z.literal(3)]);
// unfortunately, we need a string version of this for the levels {} map because
// keys wind up as strings. In retrospect, we shouldn't have used numbers but the
// die is cast.
const RowLevelString = zod_1.z.union([zod_1.z.literal('1'), zod_1.z.literal('2'), zod_1.z.literal('3')]);
const Levels = zod_1.z.record(RowLevelString, LevelConfig.optional());
const ScriptImportRule = zod_1.z.object({
    rowTypeId: RundownRowType.optional(),
    rowLevel: RowLevel.optional(),
    columnId: zod_1.z.string(),
});
const ImportableBlock = zod_1.z.enum([
    'action',
    'slug',
    'dialogue',
    'bracket',
    'sceneHeading',
    'character',
    'new_act',
    'parenthetical',
]);
const ContextBlock = zod_1.z.enum(['slug', 'sceneHeading', 'character', 'new_act']);
const BaseImportRule = zod_1.z.object({
    nodeType: ImportableBlock,
    rowTypeId: RundownRowType.optional(),
    rowLevel: RowLevel.optional(),
    contextColumns: zod_1.z.record(ContextBlock, zod_1.z.string().optional()).optional(),
    elementNumberColumnId: zod_1.z.string().optional(),
});
const SimpleImportRule = BaseImportRule.extend({
    ruleType: zod_1.z.literal('simple'),
    columnId: zod_1.z.string(),
});
const BracketImportRule = BaseImportRule.extend({
    ruleType: zod_1.z.literal('bracket-parse'),
    nodeType: zod_1.z.literal('bracket'),
    contentColumnId: zod_1.z.string(),
    bracketTypeColumnId: zod_1.z.string(),
    timeColumnId: zod_1.z.string(),
});
const SlugImportRule = BaseImportRule.extend({
    ruleType: zod_1.z.literal('slug-with-timing'),
    nodeType: zod_1.z.literal('slug'),
    columnId: zod_1.z.string(),
    durationColumnId: zod_1.z.string(),
});
const RowImportRule = zod_1.z.discriminatedUnion('ruleType', [
    SimpleImportRule,
    SlugImportRule,
    BracketImportRule,
]);
exports.RundownSchema = zod_1.z.object({
    columns: RundownColDef.array(),
    durationMode: zod_1.z.enum(['target-length', 'start-end-time']).optional(),
    primaryDurationField: GridBlobKey.optional(),
    levels: Levels,
    importRules: zod_1.z.object({
        script: ScriptImportRule,
        rows: RowImportRule.array(),
    }),
});
